import { useCallback } from 'react';
import { addMinutes, isAfter } from 'date-fns';

const SESSION_KEY = 'lov_booking_session';
const SESSION_TTL_MINUTES = 30;

interface BookingSession {
  data: Record<string, unknown>;
  timestamp: string; // ISO date
  expiresAt: string; // ISO date, 30 min from timestamp
  completedSections: string[];
  currentSection: string;
}

export function useBookingSessionPersistence() {
  // saveSession - Save form data with TTL
  const saveSession = useCallback((sessionData: Partial<BookingSession>) => {
    const now = new Date();
    const session: BookingSession = {
      data: sessionData.data ?? {},
      timestamp: now.toISOString(),
      expiresAt: addMinutes(now, SESSION_TTL_MINUTES).toISOString(),
      completedSections: sessionData.completedSections ?? [],
      currentSection: sessionData.currentSection ?? 'venue',
    };
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
    } catch (error) {
      console.warn('Failed to save booking session:', error);
    }
  }, []);

  // loadSession - Load and validate TTL
  const loadSession = useCallback((): BookingSession | null => {
    try {
      const stored = sessionStorage.getItem(SESSION_KEY);
      if (!stored) return null;

      const session: BookingSession = JSON.parse(stored);
      const expiresAt = new Date(session.expiresAt);

      if (isAfter(new Date(), expiresAt)) {
        clearSession();
        return null; // Expired
      }

      return session;
    } catch (error) {
      console.warn('Failed to load booking session:', error);
      return null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // clearSession - Remove from storage
  const clearSession = useCallback(() => {
    sessionStorage.removeItem(SESSION_KEY);
  }, []);

  // updateSectionData - Partial update with TTL reset
  const updateSectionData = useCallback(
    (section: string, data: unknown) => {
      const current = loadSession() || {
        data: {},
        completedSections: [],
        currentSection: 'venue',
        timestamp: new Date().toISOString(),
        expiresAt: addMinutes(new Date(), SESSION_TTL_MINUTES).toISOString(),
      };
      saveSession({
        ...current,
        data: { ...current.data, [section]: data },
      });
    },
    [loadSession, saveSession]
  );

  // hasValidSession - Check if resumable session exists
  const hasValidSession = useCallback((): boolean => {
    return loadSession() !== null;
  }, [loadSession]);

  return {
    saveSession,
    loadSession,
    clearSession,
    updateSectionData,
    hasValidSession,
  };
}
